// This file implements a singleton that will get correct notification information from either
// the Capacitor local notifications plugin or the server, depending on whether we're running in
// a web browser or a native app.

import { NotificationSettings, NotificationsManagerImplementation } from '.';
import { UserAlertDay, UserAlertFrequency } from '../../../../../backend/src/types.generated';

const NOTIFICATION_CHANNEL_ID = 'bin-notifications';
const NOTIFICATION_SETTINGS_KEY = 'bin-notification-settings';

const calculateNextNotificationDate = (
	day: UserAlertDay,
	time: string,
	frequency: UserAlertFrequency
): Date => {
	const [hourStr, minuteStr] = time.split(':');
	const hour = parseInt(hourStr);
	const minute = parseInt(minuteStr);

	const now = new Date();
	const nextDate = new Date();

	// Map UserAlertDay to JavaScript day (0 = Sunday, 6 = Saturday)
	const targetDay = (() => {
		switch (day) {
			case UserAlertDay.Sunday:
				return 0;
			case UserAlertDay.Monday:
				return 1;
			case UserAlertDay.Tuesday:
				return 2;
			case UserAlertDay.Wednesday:
				return 3;
			case UserAlertDay.Thursday:
				return 4;
			case UserAlertDay.Friday:
				return 5;
			case UserAlertDay.Saturday:
				return 6;
			default:
				throw new Error(`Invalid day: ${day}`);
		}
	})();

	// Set target time
	nextDate.setHours(hour, minute, 0, 0);

	// Calculate days until next occurrence
	const currentDay = now.getDay();
	let daysUntil = targetDay - currentDay;

	// If target day is today but time has passed, or target day is in the past
	if (daysUntil < 0 || (daysUntil === 0 && now > nextDate)) {
		// Add appropriate interval based on frequency
		switch (frequency) {
			case UserAlertFrequency.Weekly:
				daysUntil += 7;
				break;
			case UserAlertFrequency.Fortnightly:
				daysUntil += 14;
				break;
			case UserAlertFrequency.Monthly:
				// Add 1 month
				nextDate.setMonth(nextDate.getMonth() + 1);
				return nextDate;
		}
	}

	nextDate.setDate(now.getDate() + daysUntil);
	return nextDate;
};

export class CapacitorNotificationsManager implements NotificationsManagerImplementation {
	protected LocalNotifications:
		| typeof import('@capacitor/local-notifications').LocalNotifications
		| undefined;
	protected Preferences: typeof import('@capacitor/preferences').Preferences | undefined;

	protected readonly ensureImports = async () => {
		if (!this.LocalNotifications) {
			const { LocalNotifications } = await import('@capacitor/local-notifications');
			this.LocalNotifications = LocalNotifications;
		}
		if (!this.Preferences) {
			const { Preferences } = await import('@capacitor/preferences');
			this.Preferences = Preferences;
		}
	};

	protected assertImportsLoaded(): asserts this is this & {
		LocalNotifications: typeof import('@capacitor/local-notifications').LocalNotifications;
		Preferences: typeof import('@capacitor/preferences').Preferences;
	} {
		if (!this.LocalNotifications || !this.Preferences)
			throw new Error('Capacitor libraries failed to import.');
	}

	protected readonly loadSettings = async (): Promise<NotificationSettings | null> => {
		await this.ensureImports();
		this.assertImportsLoaded();

		const { value } = await this.Preferences.get({ key: NOTIFICATION_SETTINGS_KEY });
		if (!value) return null;

		return JSON.parse(value);
	};

	protected readonly saveSettings = async (settings: NotificationSettings) => {
		await this.ensureImports();
		this.assertImportsLoaded();

		await this.Preferences.set({ key: NOTIFICATION_SETTINGS_KEY, value: JSON.stringify(settings) });
	};

	scheduleMultipleNotifications = async (settings: NotificationSettings) => {
		const notifications = [];
		const nextDate = calculateNextNotificationDate(
			settings.day!,
			settings.time!,
			settings.frequency!
		);

		// Schedule the next 12 notifications (covers ~3 months for weekly, ~6 months for fortnightly, ~1 year for monthly)
		for (let i = 0; i < 12; i++) {
			const notificationDate = new Date(nextDate);

			// Add appropriate interval for each future notification
			switch (settings.frequency!) {
				case UserAlertFrequency.Weekly:
					notificationDate.setDate(notificationDate.getDate() + i * 7);
					break;
				case UserAlertFrequency.Fortnightly:
					notificationDate.setDate(notificationDate.getDate() + i * 14);
					break;
				case UserAlertFrequency.Monthly:
					notificationDate.setMonth(notificationDate.getMonth() + i);
					break;
			}

			notifications.push({
				id: 100 + i, // Use different IDs for each notification
				title: 'Bin Reminder',
				body: `Don't forget to take out your bins!`,
				channelId: NOTIFICATION_CHANNEL_ID,
				smallIcon: 'ic_launcher',
				largeIcon: 'ic_launcher',
				schedule: { at: notificationDate },
			});
		}

		return notifications;
	};

	public readonly ensureNotificationsScheduled = async (settings?: NotificationSettings) => {
		await this.ensureImports();
		this.assertImportsLoaded();

		const resolvedSettings = settings ?? (await this.loadSettings()) ?? {};

		console.log('Clearing all notifications.');
		const { notifications } = await this.LocalNotifications.getPending();
		if (notifications.length > 0) await this.LocalNotifications.cancel({ notifications });

		if (
			resolvedSettings.enabled &&
			resolvedSettings.frequency &&
			resolvedSettings.day &&
			resolvedSettings.time
		) {
			// Create notification channel first (required for Android)
			await this.LocalNotifications.createChannel({
				id: NOTIFICATION_CHANNEL_ID,
				name: 'Bin Reminders',
				description: 'Notifications for bin collection reminders',
				importance: 5,
				visibility: 1,
				sound: 'bin_reminder.wav',
			});

			console.log('Setting multiple notifications');

			// Schedule multiple future notifications
			const notificationsToSchedule = await this.scheduleMultipleNotifications(resolvedSettings);

			await this.LocalNotifications.schedule({
				notifications: notificationsToSchedule,
			});

			// Check what's actually scheduled
			const { notifications: pending } = await this.LocalNotifications.getPending();
			console.log('📱 Pending notifications:', JSON.stringify(pending, null, 2));
		}
	};

	readonly notificationsEnabled = async () => {
		await this.ensureImports();
		this.assertImportsLoaded();

		const permissions = await this.LocalNotifications.checkPermissions();
		const settings = await this.loadSettings();
		return (permissions.display === 'granted' && settings?.enabled) ?? false;
	};

	readonly listNotifications = async () => {
		await this.ensureImports();
		this.assertImportsLoaded();

		const settings = await this.loadSettings();

		// If notifications are enabled and configured, return the user's settings
		if (settings?.enabled && settings?.frequency && settings?.day && settings?.time) {
			return [
				{
					id: '1', // Use a consistent ID for the configured notification
					frequency: settings.frequency,
					day: settings.day,
					time: settings.time,
					from: new Date(), // Use current date as the "from" date
				},
			];
		}

		// If no notifications are configured, return empty array
		return [];
	};

	readonly notificationSettingsValuesChanged = async (
		_: { id: string },
		notificationSettings: NotificationSettings
	) => {
		await this.ensureImports();
		this.assertImportsLoaded();

		if (notificationSettings.enabled) {
			const status = await this.LocalNotifications.checkPermissions();
			if (status.display !== 'granted') {
				await this.LocalNotifications.requestPermissions();
			}
		}

		await this.saveSettings(notificationSettings);
		await this.ensureNotificationsScheduled(notificationSettings);

		return notificationSettings;
	};

	readonly notificationSettings = async () => {
		await this.ensureImports();
		this.assertImportsLoaded();

		return await this.loadSettings();
	};
}
